import React, { useState, useCallback, useMemo } from 'react'

import { cpf, cnpj } from 'cpf-cnpj-validator'

import InputMask from '../InputMask'
import { formatCpf, formatCnpj, sanitizeValue } from './helpers'

const InputCpfCnpj: React.FC<InputCpfCnpjProps> = ({
  onChange,
  initialData,
  mode = 'both',
  ...props
}) => {
  const [value, setValue] = useState<string | null>(initialData || null)

  // react-number-format strips non-digit characters before calling `format`
  // unless a custom `removeFormatting` is provided, so alphanumeric CNPJ
  // characters need to be preserved here
  const handleRemoveFormatting = (val: string): string =>
    mode === 'cpf' ? sanitizeValue(val) : cnpj.strip(val)

  const handleFormat = (val: string | number): string => {
    if (mode === 'cpf') return formatCpf(String(val))

    if (mode === 'cnpj') return formatCnpj(String(val))

    // mode === 'both' - auto-detect: letters or length > 11 means CNPJ
    const cleanValue = String(val)
    const isCnpjValue = /[A-Z]/i.test(cleanValue) || cleanValue.length > 11

    return isCnpjValue ? formatCnpj(cleanValue) : formatCpf(cleanValue)
  }

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target

      setValue(value)

      onChange?.(e)
    },
    [onChange]
  )

  const isValid = useMemo(() => {
    if (!value) return false

    if (mode === 'cpf') return cpf.isValid(value)

    if (mode === 'cnpj') return cnpj.isValid(value)

    // mode === 'both'
    return cpf.isValid(value) || cnpj.isValid(value)
  }, [value, mode])

  return (
    <InputMask
      name={props.name}
      {...props}
      format={handleFormat}
      removeFormatting={handleRemoveFormatting}
      onChange={handleChange}
      showBadgeIsValid={isValid}
      showBadgeIsInvalid={!isValid && props.required}
    />
  )
}

export default InputCpfCnpj
